/**
 * Trade route layer renderer — ArcLayer with great-circle interpolation.
 *
 * Uses resolved trade route segments from static data imports.
 * Matches WM's createTradeRoutesLayer() parameters: status-based coloring,
 * category-based width, greatCircle arcs, pickable:false.
 */

import { ArcLayer } from '@deck.gl/layers';
import type { Layer } from '@deck.gl/core';
import { resolveTradeRouteSegments } from '@/config/data';
import type { TradeRouteSegment } from '@/config/data';
import { TRADE_ROUTE_COLORS } from '@/map/layer-colors';

/** Layer IDs handled by this renderer. */
export const TRADE_ROUTE_LAYER_IDS = new Set([
  'trade-routes',
]);

// ---------------------------------------------------------------------------
// Resolved segments (computed once, reused across rebuilds)
// ---------------------------------------------------------------------------

let cachedSegments: TradeRouteSegment[] | null = null;

function getSegments(): TradeRouteSegment[] {
  if (!cachedSegments) {
    cachedSegments = resolveTradeRouteSegments();
  }
  return cachedSegments;
}

// ---------------------------------------------------------------------------
// Status-based color selection (WM dark-mode values)
// ---------------------------------------------------------------------------

type RGBA = [number, number, number, number];

function colorForStatus(status: string): RGBA {
  if (status === 'disrupted') return TRADE_ROUTE_COLORS.disrupted.source as unknown as RGBA;
  if (status === 'high_risk') return TRADE_ROUTE_COLORS.highRisk.source as unknown as RGBA;
  return TRADE_ROUTE_COLORS.default.source as unknown as RGBA;
}

// ---------------------------------------------------------------------------
// Layer factory
// ---------------------------------------------------------------------------

/**
 * Create ArcLayer(s) for all active trade route layers.
 * Returns one ArcLayer containing all resolved arc segments.
 * Uses WM parameters: status-based colors, category-based width,
 * greatCircle:true, pickable:false.
 */
export function createTradeRouteLayers(
  activeLayerIds: string[],
  _zoom: number,
): Layer[] {
  const layers: Layer[] = [];

  for (const layerId of activeLayerIds) {
    if (!TRADE_ROUTE_LAYER_IDS.has(layerId)) continue;

    const segments = getSegments();

    layers.push(
      new ArcLayer<TradeRouteSegment>({
        id: `trade-route-${layerId}`,
        data: segments,
        getSourcePosition: (d) => d.sourcePosition,
        getTargetPosition: (d) => d.targetPosition,
        getSourceColor: (d) => colorForStatus(d.status),
        getTargetColor: (d) => colorForStatus(d.status),
        getWidth: (d) => d.category === 'energy' ? 3 : 2,
        widthMinPixels: 1,
        widthMaxPixels: 6,
        greatCircle: true,
        pickable: false,
      }),
    );
  }

  return layers;
}
